import React, {useLayoutEffect, useRef, useState} from 'react'
import ReactDOM from 'react-dom'

import {DatePicker, FormRow} from '../UI/forms/index.js'
import type {DatePickerProps, DatePickerRef} from '../UI/forms/index.js'
import SearchBox from './SearchBox.js'
import SearchItem from './SearchItem.js'
import TimeSlider from './TimeSlider.js'
import {getDateChange} from './dates.js'
import {addQuery, removeQuery} from './query.js'
import {chip, searchBar} from './styles.js'
import type {Query, SearchBarColumns} from './types.js'

export type {Operator, Query, SearchBarColumn, SearchBarColumns} from './types.js'

export interface SearchBarProps {
    queries: Query[]
    /** Called with a new array (the given one is never changed) when a chip is added or removed */
    onQueryChange?: (queries: Query[]) => void
    /** The table columns that can be searched in: those with a title and a key are listed */
    columns: SearchBarColumns
    /** YYYY-MM-DD; the date field is shown when both are set (range) or always without dateRange */
    startDate?: string
    endDate?: string
    /** 'HH:mm'; the time slider is shown when both are set */
    startTime?: string
    endTime?: string
    startInclude?: boolean
    endInclude?: boolean
    /** A single date is reported as startDate === endDate */
    onDateChange?: (startDate: string, endDate: string) => void
    onTimeChange?: (startTime: string, endTime: string, startInclude: boolean, endInclude: boolean) => void
    /** Default true */
    showSearch?: boolean
    minDate?: string
    maxDate?: string
    /** In days */
    maxRange?: number
    /** Default true: a date range, false: a single date */
    dateRange?: boolean
}

/**
 * The search bar of an overview header: a date (range) field, the search field and, with times, the time slider below
 * them. The search chips are rendered in their own full width row directly below the header the SearchBar is placed
 * in, so they can't widen the header and push the buttons next to it off screen.
 */
export default function SearchBar(props: SearchBarProps) {
    const {queries, onQueryChange, columns, startDate, endDate, startTime, endTime, startInclude, endInclude, onDateChange, onTimeChange, showSearch = true, minDate, maxDate, maxRange, dateRange = true} = props

    const rootRef = useRef<HTMLDivElement>(null)
    const datePickerRef = useRef<DatePickerRef>(null)

    // The row the chips are portalled into, placed after the header (the SearchBar's parent) with its background
    const [chipsContainer] = useState(() => document.createElement('div'))
    useLayoutEffect(() => {
        const header = rootRef.current?.parentElement

        if (header) {
            chipsContainer.style.flexShrink = '0'
            chipsContainer.style.background = window.getComputedStyle(header).backgroundColor
            header.insertAdjacentElement('afterend', chipsContainer)
        }

        return () => chipsContainer.remove()
    }, [])

    const onAdd = (query: Query) => onQueryChange?.(addQuery(queries, query))

    const onRemove = (index: number) => onQueryChange?.(removeQuery(queries, index))

    const onRemoveLast = () => {
        if (queries?.length) {
            onRemove(queries.length - 1)
        }
    }

    const onDatePickerChange: DatePickerProps['onChange'] = (eventOrRange, noBubble) => {
        const change = getDateChange(eventOrRange, noBubble)

        if (change) {
            onDateChange?.(change.startDate, change.endDate)

            if (dateRange) {
                datePickerRef.current?.close()
            }
        }
    }

    const showDate = dateRange ? !!(startDate && endDate) : true

    return (
        <div ref={rootRef} style={{minWidth: onDateChange ? searchBar.minWidthWithDate : searchBar.minWidth, marginRight: searchBar.marginRight}}>
            <FormRow style={{paddingBottom: showSearch ? searchBar.searchPaddingBottom : 0}}>
                {showDate &&
                    <DatePicker
                        ref={datePickerRef}
                        label='Datum'
                        // DatePicker grows (flex 1) by default, the date has a fixed width next to the search field
                        style={{width: searchBar.dateWidth, flex: 'none'}}
                        value={startDate}
                        range={dateRange ? {startDate, endDate} : undefined}
                        minDate={minDate}
                        maxDate={maxDate}
                        maxRange={dateRange ? maxRange : undefined}
                        onChange={onDatePickerChange}
                    />
                }

                {showSearch &&
                    <SearchBox
                        columns={columns}
                        onAdd={onAdd}
                        onRemoveLast={onRemoveLast}
                    />
                }
            </FormRow>

            {startTime && endTime &&
                <TimeSlider
                    startTime={startTime}
                    endTime={endTime}
                    startInclude={startInclude}
                    endInclude={endInclude}
                    onTimeChange={onTimeChange}
                />
            }

            {queries?.length > 0 && ReactDOM.createPortal(
                <div role='group' aria-label='Zoekopdrachten' style={{display: 'flex', flexWrap: 'wrap', gap: chip.gap, padding: chip.gap}}>
                    {queries.map((query, index) => (
                        <SearchItem
                            key={`${index}-${query.key}-${query.operator}-${query.text}`}
                            query={query}
                            onRemove={() => onRemove(index)}
                        />
                    ))}
                </div>,
                chipsContainer
            )}
        </div>
    )
}
